/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
import { Directive, DoCheck, ElementRef, Input, OnChanges, Renderer2, SimpleChanges } from '@angular/core';
import difference from 'lodash-es/difference';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NToggle } from '@df/ui/toggle/toggle.service';
import { StateReaderService } from './state-reader.service';

type TClasEntries = { 0: string[]; 1: string[] };
@Directive({
    selector: '[toggleClass]',
    standalone: true
})
export class ToggleClassDirective<T> implements OnChanges, DoCheck {
    /**
     * Class list definition
     */
    @Input()
    toggleClass!: string | ((value: T) => string[]);

    /**
     * Toggling element
     */
    @Input()
    toggleClassBy!: BehaviorSubject<T> | NToggle.TGroupId | NToggle.TId | T;

    /**
     * Alias for toggleClassBy when using `[toggle]` directive
     */
    @Input()
    set toggle(value: BehaviorSubject<T> | NToggle.TGroupId | NToggle.TId | T) {
        this.toggleClassBy = value;
    }

    /**
     * Toggle state
     */
    @Input()
    toggleClassActive?: T;

    /**
     * Normalized
     */
    private _toggleClassBy!: BehaviorSubject<T>;

    /**
     * State subscription
     */
    private subscription?: Subscription;

    /**
     * Parsed classes list
     */
    private classEntries: TClasEntries = { 0: [], 1: [] };

    /**
     * Applied classes
     */
    private classes: string[] = [];

    /**
     * Dependencies
     */
    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private stateReaderService: StateReaderService
    ) {}

    /**
     * On changes hook
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['toggleClass']) {
            const classEntries: TClasEntries = { 0: [], 1: [] };
            if (typeof this.toggleClass === 'string') {
                this.toggleClass
                    .split(' ')
                    .map(i => i.trim())
                    .forEach(i => {
                        if (i[0] === '!') {
                            classEntries[0].push(i.substring(1));
                        } else {
                            classEntries[1].push(i);
                        }
                    });
            }
            this.classEntries = classEntries;
            this.applyClasses();
        }

        if (changes['toggleClassBy'] || changes['toggle']) {
            this.subscription?.unsubscribe();
            this._toggleClassBy = this.stateReaderService.toState<T>(this.toggleClassBy);
            this.subscription = this._toggleClassBy.subscribe(() => this.applyClasses());
        }

        if (changes['toggleClassActive']) {
            this._toggleClassBy.next(this.toggleClassActive!); // I am not sure what is the idea for this, worth double checking
        }
    }

    ngDoCheck() {
        this.applyClasses();
    }

    private applyClasses() {
        const value = this._toggleClassBy?.value;
        const classes = typeof this.toggleClass === 'string' ? this.classEntries[value ? 1 : 0] : this.toggleClass(value);

        difference(this.classes, classes).forEach(className => this.renderer.removeClass(this.elementRef.nativeElement, className));
        difference(classes, this.classes).forEach(className => this.renderer.addClass(this.elementRef.nativeElement, className));
        this.classes = classes;
    }
}
